import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Field,
  Input,
  PageHeader,
  Select,
  SeverityBadge,
  Spinner,
  Textarea,
} from '@aigovops/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useOptionalWorkspace } from '../lib/workspace';
import {
  getIncident,
  markObligationNotified,
  patchIncident,
  type IncidentDetail as Incident,
  type IncidentObligation,
  type IncidentPatch,
  type LossKind,
} from '../lib/api';
import { fmtDate } from '../lib/format';
import { queryKeys } from '../lib/query-keys';
import { ActionDialog, errorText, useAnnounce } from '../components/today/shared';

/**
 * /w/:slug/app/incidents/:incidentId — V111-3-5 (ADR-0093).
 *
 * Regulator notification deadlines lead (they are the time-critical part);
 * then the loss ledger an underwriter prices on. A closed incident is a
 * read-only record. Overdue is derived by the server, never stored.
 */

const STATUS_ACTIONS: Record<string, Array<{ to: NonNullable<IncidentPatch['status']>; label: string }>> = {
  open: [{ to: 'investigating', label: 'Start investigating' }, { to: 'resolved', label: 'Mark resolved' }],
  investigating: [{ to: 'resolved', label: 'Mark resolved' }],
  resolved: [{ to: 'closed', label: 'Close incident' }, { to: 'investigating', label: 'Reopen' }],
  closed: [],
};

const STATUS_WORD: Record<string, string> = { open: 'Open', investigating: 'Investigating', resolved: 'Resolved', closed: 'Closed' };

const DATA_CLASSES: Array<{ value: string; label: string }> = [
  { value: 'pii', label: 'Personal data' },
  { value: 'phi', label: 'Health records (PHI)' },
  { value: 'health_data', label: 'Other health data' },
  { value: 'financial', label: 'Financial data' },
  { value: 'employment', label: 'Employment decisions' },
];
const CLASS_LABEL = Object.fromEntries(DATA_CLASSES.map((d) => [d.value, d.label])) as Record<string, string>;

const LOSS_KINDS: Array<{ value: LossKind; label: string }> = [
  { value: 'direct', label: 'Direct loss' },
  { value: 'legal', label: 'Legal costs' },
  { value: 'regulatory', label: 'Regulatory fines' },
  { value: 'remediation', label: 'Remediation' },
];
const LOSS_LABEL = Object.fromEntries(LOSS_KINDS.map((k) => [k.value, k.label])) as Record<string, string>;

const sectionStyle: React.CSSProperties = {
  border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', background: 'var(--surface)',
  padding: 'var(--space-4)', display: 'grid', gap: 'var(--space-3)',
};
const h2Style: React.CSSProperties = { margin: 0, fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--text)' };

const money = (cents: number, currency: string) =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(cents / 100);

/** Local datetime-local value for "now". */
const nowLocal = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

export function IncidentDetail() {
  const { incidentId = '' } = useParams();
  const ws = useOptionalWorkspace();
  const slug = ws?.workspace.slug ?? '';
  const qc = useQueryClient();
  const key = [...queryKeys.incidents(slug), incidentId] as const;
  const q = useQuery({ queryKey: key, queryFn: () => getIncident(slug, incidentId), enabled: !!slug && !!incidentId });
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const [impactDirty, setImpactDirty] = useState(false);
  const [live, announce] = useAnnounce();
  useEffect(() => {
    if (!impactDirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [impactDirty]);

  const refresh = () => qc.invalidateQueries({ queryKey: queryKeys.incidents(slug) });

  // Arriving from the list's overdue chip (#inc-deadlines) or a new report (#inc-impact): land there.
  const { hash } = useLocation();
  const loaded = !!q.data;
  useEffect(() => {
    if (!loaded) return;
    const id = hash === '#inc-impact' ? 'inc-impact-h' : hash === '#inc-deadlines' ? 'inc-deadlines' : null;
    const el = id ? document.getElementById(id) : null;
    if (el) { el.scrollIntoView({ block: 'start' }); el.focus(); }
  }, [loaded, hash]);

  if (q.isLoading) return <Spinner label="Loading incident" />;
  if (q.error || !q.data) {
    return (
      <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
        <Alert variant="error">{q.error?.message ?? 'Incident not found.'}</Alert>
        <Link to={`/w/${slug}/app/incidents`}>Back to incidents</Link>
      </div>
    );
  }
  const { incident, obligations } = q.data;
  const openDeadlines = obligations.filter((o) => o.status !== 'notified');

  async function transition(to: NonNullable<IncidentPatch['status']>): Promise<boolean> {
    setStatusBusy(true);
    setStatusError(null);
    try {
      await patchIncident(slug, incident.incident_id, { status: to });
      await refresh();
      setConfirmClose(false);
      announce(`Incident is now ${STATUS_WORD[to]?.toLowerCase() ?? to}.`);
      setTimeout(() => document.getElementById('inc-deadlines')?.focus(), 0);
      return true;
    } catch (err) {
      setStatusError(errorText(err, 'Could not change the status.'));
      return false;
    } finally {
      setStatusBusy(false);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: '64rem' }}>
      {live}
      <Link to={`/w/${slug}/app/incidents`} style={{ fontSize: 'var(--font-size-2xs)', color: 'var(--text-muted)' }}>
        ← Incidents
      </Link>
      <PageHeader
        title={incident.title}
        subtitle={`Reported ${fmtDate(incident.reported_at)}${incident.jurisdiction ? ` · ${incident.jurisdiction}` : ''}`}
        right={
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            {(STATUS_ACTIONS[incident.status] ?? []).map((n) => (
              <Button key={n.to} variant="secondary" compact disabled={statusBusy}
                onClick={() => (n.to === 'closed' && (openDeadlines.length > 0 || impactDirty) ? setConfirmClose(true) : void transition(n.to))}>
                {n.label}
              </Button>
            ))}
          </div>
        }
      />
      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
        <SeverityBadge severity={mapSeverity(incident.severity)} />
        <Badge tone={incident.status === 'closed' || incident.status === 'resolved' ? 'neutral' : 'accent'}>{STATUS_WORD[incident.status] ?? incident.status}</Badge>
        {incident.legal_privileged ? <Badge tone="neutral">Privileged</Badge> : null}
      </div>
      {statusError ? <Alert variant="error">{statusError}</Alert> : null}

      <ObligationsSection slug={slug} incident={incident} obligations={obligations} onChanged={refresh} announce={announce} />
      {incident.status === 'closed'
        ? <LedgerRecord incident={incident} />
        : <LedgerSection slug={slug} incident={incident} onSaved={refresh} announce={announce} onDirtyChange={setImpactDirty} />}

      <ActionDialog
        open={confirmClose}
        title={openDeadlines.length > 0 ? 'Close with notifications still open?' : 'Close without saving your impact edits?'}
        submitLabel={impactDirty ? 'Close and discard edits' : 'Close incident'}
        cancelLabel={impactDirty ? 'Keep editing' : 'Cancel'}
        danger
        onClose={() => setConfirmClose(false)}
        onSubmit={async () => {
          const ok = await transition('closed');
          if (ok) setImpactDirty(false);
          return ok;
        }}
        busy={statusBusy}
        error={statusError}
        subtitle="A closed incident is a record and can’t be edited afterwards."
      >
        <div style={{ display: 'grid', gap: 'var(--space-2)', color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }}>
          {impactDirty ? (
            <p style={{ margin: 0 }}>
              <strong>Your unsaved impact and loss edits will be lost.</strong> Keep editing and choose Save impact first to keep them.
            </p>
          ) : null}
          {openDeadlines.length > 0 ? (
            <>
              <p style={{ margin: 0 }}>These deadlines can’t be marked notified afterwards:</p>
              <ul style={{ margin: 0, paddingLeft: 'var(--space-4)' }}>
                {openDeadlines.map((o) => <li key={o.obligation_id}>{o.regulator} · {o.statute_ref}</li>)}
              </ul>
            </>
          ) : null}
        </div>
      </ActionDialog>
    </div>
  );
}

function ObligationsSection({
  slug, incident, obligations, onChanged, announce,
}: { slug: string; incident: Incident; obligations: IncidentObligation[]; onChanged: () => Promise<unknown>; announce: (m: string) => void }) {
  const [target, setTarget] = useState<IncidentObligation | null>(null);
  const [when, setWhen] = useState(nowLocal());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sorted = useMemo(
    () => [...obligations].sort((a, b) => rank(a.status) - rank(b.status) || a.deadline_at.localeCompare(b.deadline_at)),
    [obligations],
  );
  const closed = incident.status === 'closed';

  async function confirm(): Promise<boolean> {
    if (!target) return false;
    const t = new Date(when);
    if (Number.isNaN(t.getTime()) || t.getTime() > Date.now() + 5 * 60_000) {
      setError('Choose when you notified them. It can’t be in the future.');
      return false;
    }
    setBusy(true);
    setError(null);
    try {
      await markObligationNotified(slug, incident.incident_id, target.obligation_id, t.toISOString());
      await onChanged();
      announce(`${target.regulator} recorded as notified.`);
      setTarget(null);
      setTimeout(() => document.getElementById('inc-deadlines')?.focus(), 0);
      return true;
    } catch (err) {
      setError(errorText(err, 'Could not record the notification.'));
      return false;
    } finally {
      setBusy(false);
    }
  }

  return (
    <section style={sectionStyle} aria-labelledby="inc-deadlines">
      <h2 id="inc-deadlines" tabIndex={-1} style={h2Style}>Notification deadlines</h2>
      {sorted.length === 0 ? (
        <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 'var(--font-size-2xs)' }}>
          No regulator deadline applies yet. Record the data involved and how many people were affected below. Deadlines appear here as soon as one applies.{' '}
          {closed ? null : <a href="#inc-impact" style={{ textDecoration: 'underline' }}>Add impact details</a>}
        </p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--space-2)' }}>
          {sorted.map((o) => (
            <li
              key={o.obligation_id}
              style={{
                display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap',
                padding: 'var(--space-3)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)',
              }}
            >
              <div style={{ flex: 1, minWidth: '14rem', display: 'grid', gap: '2px' }}>
                <span style={{ fontWeight: 600, color: 'var(--text)' }}>{o.regulator}</span>
                <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>{o.statute_ref}</span>
              </div>
              <DeadlineLabel o={o} />
              {o.status === 'notified' || closed ? null : (
                <Button variant={o.status === 'overdue' ? 'primary' : 'secondary'} compact onClick={() => { setTarget(o); setWhen(nowLocal()); setError(null); }}>
                  Mark notified<span className="sr-only">: {o.regulator}</span>
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      <ActionDialog
        open={target !== null}
        title={target ? `Record notification to ${target.regulator}` : 'Record notification'}
        submitLabel="Record notification"
        onClose={() => setTarget(null)}
        onSubmit={confirm}
        busy={busy}
        error={error}
        subtitle={target ? target.statute_ref : undefined}
      >
        <Field id="inc-notified-at" label="When you notified them">
          <Input id="inc-notified-at" type="datetime-local" value={when} max={nowLocal()} onChange={(e) => setWhen(e.target.value)} />
        </Field>
      </ActionDialog>
    </section>
  );
}

const PILL: Record<IncidentObligation['status'], { glyph: string; fg: string; bg: string }> = {
  overdue: { glyph: '✕', fg: 'var(--danger-fg)', bg: 'var(--danger-bg)' },
  pending: { glyph: '⏱', fg: 'var(--warning-fg)', bg: 'var(--warning-bg)' },
  notified: { glyph: '✓', fg: 'var(--text-muted)', bg: 'var(--surface-elevated)' },
};

/** Status carried by glyph + words, never color alone. */
function DeadlineLabel({ o }: { o: IncidentObligation }) {
  const p = PILL[o.status];
  const left = (Date.parse(o.deadline_at) - Date.now()) / 3_600_000;
  const remaining = left > 48 ? `${Math.round(left / 24)} days left` : `${Math.max(1, Math.round(left))} hours left`;
  const text =
    o.status === 'notified' ? `Notified ${fmtDate(o.notified_at)}`
      : o.status === 'overdue' ? `Overdue · was due ${fmtDate(o.deadline_at)}`
        : `Due ${fmtDate(o.deadline_at)} · ${remaining}`;
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)',
        padding: '2px var(--space-2)', borderRadius: 'var(--radius-chip)',
        background: p.bg, color: p.fg, fontSize: 'var(--font-size-3xs)', fontWeight: 600, whiteSpace: 'nowrap',
      }}
    >
      <span aria-hidden="true">{p.glyph}</span>
      {text}
    </span>
  );
}

function formOf(incident: Incident) {
  return {
    occurred_at: incident.occurred_at?.slice(0, 10) ?? '',
    discovered_at: incident.discovered_at?.slice(0, 10) ?? '',
    root_cause: incident.root_cause ?? '',
    loss: incident.loss_amount_cents != null ? String(incident.loss_amount_cents / 100) : '',
    loss_currency: incident.loss_currency ?? 'USD',
    loss_kind: (incident.loss_kind ?? '') as LossKind | '',
    affected: incident.affected_persons_count != null ? String(incident.affected_persons_count) : '',
    data_classes: parseClasses(incident.data_classes_json),
    outcome: incident.outcome ?? '',
  };
}

function LedgerSection({ slug, incident, onSaved, announce, onDirtyChange }: {
  slug: string; incident: Incident; onSaved: () => Promise<unknown>; announce: (m: string) => void; onDirtyChange: (dirty: boolean) => void;
}) {
  const initial = useMemo(() => formOf(incident), [incident]);
  const [f, setF] = useState(initial);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { onDirtyChange(dirty); }, [dirty, onDirtyChange]);
  // A refetch (after Mark notified / a status change) never wipes unsaved edits.
  useEffect(() => { if (!dirty) setF(initial); }, [initial, dirty]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const edit = (patch: Partial<typeof f>) => { setF({ ...f, ...patch }); setDirty(true); };

  async function save() {
    setBusy(true);
    setError(null);
    const loss = f.loss.trim() === '' ? null : Math.round(Number(f.loss.replace(/,/g, '')) * 100);
    const affected = f.affected.trim() === '' ? null : Number(f.affected.replace(/,/g, ''));
    if ((loss !== null && (!Number.isFinite(loss) || loss < 0)) || (affected !== null && (!Number.isInteger(affected) || affected < 0))) {
      setError('Loss and people affected must be zero or more.');
      setBusy(false);
      return;
    }
    try {
      await patchIncident(slug, incident.incident_id, {
        occurred_at: f.occurred_at ? `${f.occurred_at}T00:00:00.000Z` : null,
        discovered_at: f.discovered_at ? `${f.discovered_at}T00:00:00.000Z` : null,
        root_cause: f.root_cause.trim() || null,
        loss_amount_cents: loss,
        loss_currency: loss === null ? null : f.loss_currency,
        loss_kind: f.loss_kind || null,
        affected_persons_count: affected,
        data_classes: f.data_classes,
        outcome: f.outcome.trim() || null,
      });
      setDirty(false);
      await onSaved();
      announce('Impact saved. Notification deadlines were recalculated.');
      setTimeout(() => document.getElementById('inc-deadlines')?.focus(), 0);
    } catch (err) {
      setError(errorText(err, 'Could not save.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section id="inc-impact" style={sectionStyle} aria-labelledby="inc-impact-h">
      <h2 id="inc-impact-h" tabIndex={-1} style={h2Style}>Impact and loss</h2>
      <div style={{ display: 'grid', gap: 'var(--space-3)', gridTemplateColumns: 'repeat(auto-fit, minmax(14rem, 1fr))' }}>
        <Field id="inc-occurred" label="Occurred on">
          <Input id="inc-occurred" type="date" value={f.occurred_at} onChange={(e) => edit({ occurred_at: e.target.value })} />
        </Field>
        <Field id="inc-discovered" label="Discovered on">
          <Input id="inc-discovered" type="date" value={f.discovered_at} onChange={(e) => edit({ discovered_at: e.target.value })} />
        </Field>
        <Field id="inc-affected" label="People affected">
          <Input id="inc-affected" inputMode="numeric" value={f.affected} onChange={(e) => edit({ affected: e.target.value })} />
        </Field>
        <Field id="inc-loss" label="Loss amount">
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Input id="inc-loss" inputMode="decimal" value={f.loss} onChange={(e) => edit({ loss: e.target.value })} />
            <Select aria-label="Currency" value={f.loss_currency} onChange={(e) => edit({ loss_currency: e.target.value })}>
              {['USD', 'EUR', 'GBP', 'CAD'].map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
          </div>
        </Field>
        <Field id="inc-loss-kind" label="Loss type">
          <Select id="inc-loss-kind" value={f.loss_kind} onChange={(e) => edit({ loss_kind: e.target.value as LossKind | '' })}>
            <option value="">Not set</option>
            {LOSS_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
          </Select>
        </Field>
        <Field id="inc-outcome" label="Outcome">
          <Input id="inc-outcome" value={f.outcome} onChange={(e) => edit({ outcome: e.target.value })} />
        </Field>
      </div>
      <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 'var(--space-2)' }}>
        <legend style={{ fontSize: 'var(--font-size-2xs)', fontWeight: 600, color: 'var(--text)', marginBottom: 'var(--space-2)' }}>Data involved</legend>
        <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
          {DATA_CLASSES.map((d) => (
            <Checkbox
              key={d.value}
              label={d.label}
              checked={f.data_classes.includes(d.value)}
              onChange={(e) => edit({ data_classes: e.target.checked ? [...f.data_classes, d.value] : f.data_classes.filter((x) => x !== d.value) })}
            />
          ))}
        </div>
      </fieldset>
      <Field id="inc-root-cause" label="Root cause">
        <Textarea id="inc-root-cause" rows={3} value={f.root_cause} onChange={(e) => edit({ root_cause: e.target.value })} />
      </Field>
      {error ? <Alert variant="error">{error}</Alert> : null}
      <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
        <Button variant="primary" compact disabled={busy || !dirty} onClick={save}>{busy ? 'Saving…' : 'Save impact'}</Button>
        {dirty ? <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>Unsaved changes</span> : null}
      </div>
    </section>
  );
}

/** A closed incident is a record: readable and selectable, not a disabled form. */
function LedgerRecord({ incident }: { incident: Incident }) {
  const classes = parseClasses(incident.data_classes_json).map((c) => CLASS_LABEL[c] ?? c);
  const rows: Array<[string, string]> = [
    ['Occurred on', fmtDate(incident.occurred_at) || '—'],
    ['Discovered on', fmtDate(incident.discovered_at) || '—'],
    ['People affected', incident.affected_persons_count != null ? incident.affected_persons_count.toLocaleString() : '—'],
    ['Loss', incident.loss_amount_cents != null ? `${money(incident.loss_amount_cents, incident.loss_currency ?? 'USD')}${incident.loss_kind ? ` · ${LOSS_LABEL[incident.loss_kind] ?? incident.loss_kind}` : ''}` : '—'],
    ['Data involved', classes.length ? classes.join(', ') : '—'],
    ['Outcome', incident.outcome ?? '—'],
    ['Root cause', incident.root_cause ?? '—'],
  ];
  return (
    <section id="inc-impact" style={sectionStyle} aria-labelledby="inc-impact-h">
      <h2 id="inc-impact-h" tabIndex={-1} style={h2Style}>Impact and loss</h2>
      <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 'var(--space-4)', rowGap: 'var(--space-2)', fontSize: 'var(--font-size-2xs)' }}>
        {rows.map(([k, v]) => (
          <div key={k} style={{ display: 'contents' }}>
            <dt style={{ color: 'var(--text-dim)' }}>{k}</dt>
            <dd style={{ margin: 0, color: 'var(--text)' }}>{v}</dd>
          </div>
        ))}
      </dl>
      <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 'var(--font-size-3xs)' }}>Closed incidents are kept as records and can’t be edited.</p>
    </section>
  );
}

function rank(s: IncidentObligation['status']): number {
  return s === 'overdue' ? 0 : s === 'pending' ? 1 : 2;
}

function parseClasses(json: string): string[] {
  try {
    const v = JSON.parse(json);
    return Array.isArray(v) ? v.map(String) : [];
  } catch {
    return [];
  }
}

function mapSeverity(s: string): 'critical' | 'high' | 'elevated' | 'moderate' | 'standard' {
  return s === 'critical' ? 'critical' : s === 'high' ? 'high' : s === 'medium' ? 'moderate' : 'standard';
}
